import { apiClient } from './api';
import { tagUser } from './telemetry';
import type {
  AuthSession,
  ChangePasswordRequest,
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  RegisterRequest,
  RequestPasswordResetRequest,
  RequestPasswordResetResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  UpdateUserProfile,
  User,
} from '@/types';
import { API_ENDPOINTS, ROUTES } from '@/constants';
import { isAdmin } from '@/types/models';

// Token storage keys
const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_EXPIRY_KEY = 'token_expiry';
const USER_KEY = 'auth_user';
const IMPERSONATED_RENTAL_KEY = 'impersonated_rental';

// The profile page renders its own toast per request; without this the
// interceptor's toast stacks on top of it (see docs/SERVICE_LAYER.md).
const SKIP_ERROR_MESSAGE_HEADERS = { 'X-Skip-Error-Message': 'true' } as const;

// Password-reset requests are public: no bearer auth even with stale local
// session data, and the page owns its own error state (see docs/SERVICE_LAYER.md).
const PUBLIC_AUTH_REQUEST_CONFIG = {
  headers: {
    'X-Skip-Auth': 'true',
    'X-Skip-Error-Message': 'true',
  },
} as const;

// Impersonation types
export type ImpersonatedRental = {
  id: string;
  name: string;
  slug: string;
};

// Token management functions
export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY);

export const getRefreshToken = (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY);

export const getTokenExpiry = (): number | null => {
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
  return expiry ? parseInt(expiry, 10) : null;
};

export const getStoredUser = (): User | null => {
  const userJson = localStorage.getItem(USER_KEY);
  if (!userJson) return null;
  try {
    return JSON.parse(userJson) as User;
  } catch {
    return null;
  }
};

export const setStoredUser = (user: User): void => {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  syncTelemetryUser();
};

/**
 * Records on the stored user whether a password someone else set must be changed first.
 * The layout guard and the login redirect read it; `/users/me` writes the server's value.
 */
export const setPasswordChangeRequired = (required: boolean): void => {
  const user = getStoredUser();
  if (!user || Boolean(user.mustChangePassword) === required) return;
  setStoredUser({ ...user, mustChangePassword: required });
};

/** True while the stored user must change a password that someone else set. */
export const isPasswordChangeRequired = (): boolean => getStoredUser()?.mustChangePassword === true;

export const storeAuthSession = (session: AuthSession): void => {
  localStorage.setItem(TOKEN_KEY, session.token);
  localStorage.setItem(REFRESH_TOKEN_KEY, session.refreshToken);
  localStorage.setItem(TOKEN_EXPIRY_KEY, session.expiresAt.toString());
  setStoredUser(session.user);
};

export const clearAuthSession = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  localStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(IMPERSONATED_RENTAL_KEY);
  tagUser(null);
};

export const isTokenExpired = (): boolean => {
  const expiry = getTokenExpiry();
  if (!expiry) return true;
  // Consider token expired if less than 60 seconds remaining
  return Date.now() >= expiry - 60000;
};

// Auth API functions
export const login = async (credentials: LoginRequest): Promise<AuthSession> => {
  const response = await apiClient.post<LoginResponse>(
    '/auth/login',
    credentials,
    PUBLIC_AUTH_REQUEST_CONFIG,
  );
  const { accessToken, refreshToken, expiresIn, user } = response.data;

  const session: AuthSession = {
    token: accessToken,
    refreshToken,
    expiresAt: Date.now() + expiresIn * 1000,
    user,
  };

  storeAuthSession(session);
  return session;
};

export const register = async (data: RegisterRequest): Promise<AuthSession> => {
  const response = await apiClient.post<LoginResponse>(
    '/auth/register',
    data,
    PUBLIC_AUTH_REQUEST_CONFIG,
  );
  const { accessToken, refreshToken, expiresIn, user } = response.data;

  const session: AuthSession = {
    token: accessToken,
    refreshToken,
    expiresAt: Date.now() + expiresIn * 1000,
    user,
  };

  storeAuthSession(session);
  return session;
};

const requestAccessToken = async (): Promise<AuthSession> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const request: RefreshTokenRequest = { refreshToken };
  // Each caller decides what to tell the user when the refresh fails.
  const response = await apiClient.post<RefreshTokenResponse>('/auth/refresh', request, {
    headers: SKIP_ERROR_MESSAGE_HEADERS,
  });
  const { accessToken, expiresIn } = response.data;

  const user = getStoredUser();
  if (!user) {
    throw new Error('No user data available');
  }

  const session: AuthSession = {
    token: accessToken,
    refreshToken, // Keep the same refresh token
    expiresAt: Date.now() + expiresIn * 1000,
    user,
  };

  storeAuthSession(session);
  return session;
};

// Concurrent 401s share one refresh: without this a page firing N parallel
// requests sends N refreshes, and the backend may rotate or revoke the token
// between them.
let refreshInFlight: Promise<AuthSession> | null = null;

export const refreshAccessToken = (): Promise<AuthSession> => {
  refreshInFlight ??= requestAccessToken().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

export const logout = async (): Promise<void> => {
  if (isTokenExpired()) {
    try {
      // The backend's auth guard rejects an expired access token before the
      // logout handler runs, so revocation would silently no-op. Refresh
      // first so the call below carries a live token. Best-effort: if the
      // refresh token is dead too, there's nothing left to revoke.
      await refreshAccessToken();
    } catch {
      // Ignore - fall through and attempt the logout call anyway.
    }
  }

  try {
    // Revoke the session server-side so a captured refresh token can't keep
    // minting access tokens after the user logs out. Best-effort: a network
    // error or an already-expired access token must not block local logout.
    await apiClient.post(API_ENDPOINTS.LOGOUT, undefined, {
      headers: SKIP_ERROR_MESSAGE_HEADERS,
    });
  } catch {
    // Ignore - local session state is cleared below regardless.
  }
  clearAuthSession();
};

export const getCurrentUser = async (): Promise<User> => {
  const response = await apiClient.get<{ user: User }>(API_ENDPOINTS.USER_PROFILE);
  const user = response.data.user;
  setStoredUser(user);

  return user;
};

/**
 * Fetch the authenticated user's profile.
 *
 * `GET /users/me` nests the profile under `user` - reading `response.data`
 * directly yields an object whose every field is undefined.
 */
export const getUserProfile = async (): Promise<User> => {
  const response = await apiClient.get<{ user: User }>(API_ENDPOINTS.USER_PROFILE, {
    headers: SKIP_ERROR_MESSAGE_HEADERS,
  });
  return response.data.user;
};

/**
 * Update the authenticated user's profile. The endpoint is PATCH, not PUT.
 *
 * Refreshes the cached user so `getStoredUser()` consumers (permissions, access
 * control) don't keep serving the pre-edit name and email.
 */
export const updateUserProfile = async (data: UpdateUserProfile): Promise<User> => {
  const response = await apiClient.patch<{ user: User }>(API_ENDPOINTS.USER_PROFILE, data, {
    headers: SKIP_ERROR_MESSAGE_HEADERS,
  });
  const user = response.data.user;
  setStoredUser(user);

  return user;
};

export const changePassword = async (data: ChangePasswordRequest): Promise<void> => {
  await apiClient.post(API_ENDPOINTS.USER_CHANGE_PASSWORD, data, {
    headers: SKIP_ERROR_MESSAGE_HEADERS,
  });
};

export const requestPasswordReset = async (
  data: RequestPasswordResetRequest,
): Promise<RequestPasswordResetResponse> => {
  const response = await apiClient.post<RequestPasswordResetResponse>(
    API_ENDPOINTS.REQUEST_PASSWORD_RESET,
    data,
    PUBLIC_AUTH_REQUEST_CONFIG,
  );
  return response.data;
};

export const resetPassword = async (data: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
  const response = await apiClient.post<ResetPasswordResponse>(
    API_ENDPOINTS.RESET_PASSWORD,
    data,
    PUBLIC_AUTH_REQUEST_CONFIG,
  );
  return response.data;
};

// Impersonation functions (using sessionStorage - clears when browser tab is closed)
export const getImpersonatedRental = (): ImpersonatedRental | null => {
  const data = sessionStorage.getItem(IMPERSONATED_RENTAL_KEY);
  if (!data) return null;
  try {
    return JSON.parse(data) as ImpersonatedRental;
  } catch {
    return null;
  }
};

export const setImpersonatedRental = (rental: ImpersonatedRental): void => {
  sessionStorage.setItem(IMPERSONATED_RENTAL_KEY, JSON.stringify(rental));
  syncTelemetryUser();
};

export const clearImpersonatedRental = (): void => {
  sessionStorage.removeItem(IMPERSONATED_RENTAL_KEY);
  syncTelemetryUser();
};

/**
 * Global view: an Admin who impersonates no Rental. It reads the stored session, not
 * the identity query: that query is empty while it loads, and it holds the previous
 * user after a logout and a login in one tab. Callers need no subscription: a login
 * mounts the layout again, and each start and end of an Impersonation is a full page load.
 */
export const isAdminInGlobalView = (): boolean =>
  isAdmin(getStoredUser()) && getImpersonatedRental() === null;

// The platform screens refuse an Impersonation (PlatformRouteGuard, GlobalVehicleModelsRouteGuard)
const PLATFORM_ROUTES = [
  ROUTES.RENTALS,
  ROUTES.SECURITY_EVENTS,
  ROUTES.ADMIN_USERS,
  ROUTES.GLOBAL_VEHICLE_MODELS,
];

const isPlatformPath = (path: string): boolean =>
  PLATFORM_ROUTES.some((route) => path === route || path.startsWith(`${route}/`));

export const startImpersonation = async (rental: ImpersonatedRental): Promise<void> => {
  try {
    await apiClient.post(API_ENDPOINTS.ADMIN_IMPERSONATE, { rentalId: rental.id });
    // Only these fields: callers pass a full Rental, and it is kept in sessionStorage
    setImpersonatedRental({ id: rental.id, name: rental.name, slug: rental.slug });
    // A full load, so all data refreshes with the new Rental in scope. A reload of a platform
    // screen would show its refusal, so those go to the Dashboard. replace keeps Back off it.
    if (isPlatformPath(window.location.pathname)) {
      window.location.replace(ROUTES.DASHBOARD);
    } else {
      window.location.reload();
    }
  } catch (error) {
    // The apiClient interceptor shows the error
    console.error('Failed to start impersonation:', error);
  }
};

export const endImpersonation = async (rentalId: string): Promise<void> => {
  try {
    await apiClient.post(API_ENDPOINTS.ADMIN_END_IMPERSONATE, { rentalId });
    clearImpersonatedRental();
    // Global view has only platform screens: a full load of the Rentals list. replace drops
    // the Rental URL from the history, so Back does not open a Rental route in Global view.
    window.location.replace(ROUTES.RENTALS);
  } catch (error) {
    // The apiClient interceptor shows the error
    console.error('Failed to end impersonation:', error);
  }
};

/**
 * Puts a saved trading name into the stored session, so the header and the
 * Impersonation banner show it without a reload. Only for the Rental in scope
 * now: false, and nothing written, when `rentalId` is no longer that Rental.
 * The ID, slug, the user's own name and the tokens stay as they are.
 */
export const syncActiveRentalName = (rentalId: string, name: string): boolean => {
  const user = getStoredUser();
  if (isAdmin(user)) {
    const impersonated = getImpersonatedRental();
    if (impersonated?.id !== rentalId) return false;
    if (impersonated.name !== name) setImpersonatedRental({ ...impersonated, name });
    return true;
  }
  if (!user?.rental || user.rental.id !== rentalId) return false;
  if (user.rental.name !== name) setStoredUser({ ...user, rental: { ...user.rental, name } });
  return true;
};

export const syncTelemetryUser = (): void => {
  const user = getStoredUser();
  tagUser(
    user
      ? {
          id: user.id,
          tenantId: getImpersonatedRental()?.id ?? user.rental?.id ?? null,
          role: user.role,
        }
      : null,
  );
};
